/**
 * `AuthProvider` is the single source of truth for "who is signed in?" in the
 * SPA. It:
 *   1. Fetches `/api/auth/config` to learn which IdPs are enabled.
 *   2. Builds one MSAL `PublicClientApplication` per enabled IdP.
 *   3. Tries `/api/auth/me` to see if an existing `cc.sid` cookie session
 *      already exists (page reload, new tab).
 *   4. Exposes `signInWithEntra` / `signInWithPassword` / `signOut` plus the
 *      current `user` to consumers via `useAuth()`.
 *
 * The id_token returned by MSAL after a successful Auth Code + PKCE exchange
 * is POSTed once to `/api/auth/session` and then discarded. No tokens are
 * persisted to localStorage. MSAL uses sessionStorage only for redirect
 * interaction state (see `msal-config.ts`).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  InteractionRequiredAuthError,
  type AuthenticationResult,
  type PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import { UserDto } from '@cloudcost/shared-types/users';
import { urls } from '@/urls';
import {
  removeUserSession as clearLegacySession,
  setUserSession as primeLegacySession,
} from '@/lib/common';
import {
  AuthBootstrapConfig,
  buildMsalInstances,
  MsalInstanceEntry,
} from './msal-config';

type Status = 'loading' | 'anonymous' | 'authenticated' | 'error';

export interface AuthContextValue {
  status: Status;
  user: UserDto | null;
  idps: MsalInstanceEntry[];
  allowLocalLogin: boolean;
  allowLocalPasswordSetup: boolean;
  error: string | null;
  signInWithEntra: (idpName?: string) => Promise<void>;
  completeLocalLogin: (user: UserDto) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REDIRECT_FLAG = 'cc.msal.redirecting';
const CSRF_COOKIE = 'cc.csrf';
const CSRF_HEADER = 'X-CSRF-Token';

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (const raw of parts) {
    const [name, ...rest] = raw.trim().split('=');
    if (name === CSRF_COOKIE) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
}

function buildJsonHeaders(method: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = readCsrfCookie();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  return headers;
}

async function readJsonOrThrow(res: Response): Promise<any> {
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const message =
      (data && (data.message || data.error)) || res.statusText || 'Request failed';
    const err = new Error(message) as any;
    err.status = res.status;
    throw err;
  }
  return data;
}

async function postJson(url: string, body: unknown): Promise<any> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: buildJsonHeaders('POST'),
    body: JSON.stringify(body),
  });
  return readJsonOrThrow(res);
}

async function getJson(url: string): Promise<any> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return readJsonOrThrow(res);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<UserDto | null>(null);
  const [entries, setEntries] = useState<MsalInstanceEntry[]>([]);
  const [allowLocalLogin, setAllowLocalLogin] = useState(false);
  const [allowLocalPasswordSetup, setAllowLocalPasswordSetup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handledRedirectFor = useRef<Set<PublicClientApplication>>(new Set());

  const exchangeIdTokenForSession = useCallback(
    async (idToken: string): Promise<UserDto> => {
      const fetched = (await postJson(urls.Auth.session, {
        id_token: idToken,
      })) as UserDto;
      return fetched;
    },
    [],
  );

  const refreshFromServer = useCallback(async (): Promise<UserDto | null> => {
    try {
      const me = (await getJson(urls.Auth.me)) as UserDto;
      setUser(me);
      primeLegacySession(me);
      setStatus('authenticated');
      return me;
    } catch (err: any) {
      if (err?.status === 401) {
        setUser(null);
        clearLegacySession();
        setStatus('anonymous');
        return null;
      }
      throw err;
    }
  }, []);

  // 1. Bootstrap config + MSAL instances + session
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = (await getJson(urls.Auth.config)) as AuthBootstrapConfig;
        const built = await buildMsalInstances(cfg.idps);
        if (cancelled) return;
        setEntries(built);
        setAllowLocalLogin(!!cfg.allowLocalLogin);
        setAllowLocalPasswordSetup(!!cfg.allowLocalPasswordSetup);

        // 2. Drain any redirect responses (Auth Code + PKCE returns here)
        for (const entry of built) {
          let result: AuthenticationResult | null = null;
          try {
            result = await entry.instance.handleRedirectPromise();
          } catch (e) {
            console.error('MSAL handleRedirectPromise failed', e);
          }
          handledRedirectFor.current.add(entry.instance);
          if (result?.account) {
            entry.instance.setActiveAccount(result.account);
          }
          if (result?.idToken) {
            try {
              window.sessionStorage.removeItem(REDIRECT_FLAG);
            } catch {
              // sessionStorage may be unavailable; ignore.
            }
            try {
              const me = await exchangeIdTokenForSession(result.idToken);
              if (cancelled) return;
              setUser(me);
              primeLegacySession(me);
              setStatus('authenticated');
              return;
            } catch (e: any) {
              if (cancelled) return;
              setError(e?.message ?? 'Sign-in failed');
              setStatus('error');
              return;
            }
          }
        }

        // 3. Otherwise check for an existing server session
        await refreshFromServer();
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? 'Failed to initialise authentication');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exchangeIdTokenForSession, refreshFromServer]);

  const signInWithEntra = useCallback(
    async (idpName?: string) => {
      const entry =
        (idpName && entries.find((e) => e.idp.name === idpName)) || entries[0];
      if (!entry) {
        setError('No identity provider is configured');
        return;
      }
      try {
        window.sessionStorage.setItem(REDIRECT_FLAG, entry.idp.name);
      } catch {
        // ignore — sessionStorage may be unavailable in some contexts.
      }
      const request: RedirectRequest = {
        scopes: entry.idp.scopes,
        prompt: 'select_account',
      };
      try {
        await entry.instance.loginRedirect(request);
      } catch (e: any) {
        if (e instanceof InteractionRequiredAuthError) {
          await entry.instance.loginRedirect(request);
          return;
        }
        setError(e?.message ?? 'Sign-in failed');
      }
    },
    [entries],
  );

  const completeLocalLogin = useCallback((u: UserDto) => {
    setUser(u);
    primeLegacySession(u);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await postJson(urls.Auth.logout, {});
    } catch {
      // Even if the server call fails, drop local state.
    }
    setUser(null);
    clearLegacySession();
    setStatus('anonymous');
    const active = entries.find((e) =>
      e.instance.getActiveAccount() !== null,
    );
    if (active) {
      try {
        await active.instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/',
        });
      } catch {
        // ignore
      }
    } else {
      window.location.href = '/';
    }
  }, [entries]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      idps: entries,
      allowLocalLogin,
      allowLocalPasswordSetup,
      error,
      signInWithEntra,
      completeLocalLogin,
      signOut,
      refresh: async () => {
        await refreshFromServer();
      },
    }),
    [
      status,
      user,
      entries,
      allowLocalLogin,
      allowLocalPasswordSetup,
      error,
      signInWithEntra,
      completeLocalLogin,
      signOut,
      refreshFromServer,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

/**
 * Convenience hook for components that only need the user. Throws if used
 * outside the provider so it stays equivalent in ergonomics to the previous
 * `getUser()` call site.
 */
export function useUser(): UserDto | null {
  return useAuth().user;
}
